// OwnNotifyBar — figure S4b's last line, inside the fund-once workbench:
//
//   You are the lead on every project here — nothing to raise.
//
// At portfolio scope the overlap workbench offers "notify both leads", because
// the two inventions in a group can belong to different people. At own scope
// they cannot: every invention in the group is the caller's own, so there is no
// one to raise it with. The design's answer is to replace the control with a
// sentence rather than render a disabled button, and this component is that
// sentence.
//
// THE SENTENCE IS DERIVED, NEVER ASSERTED. It is rendered only when it is
// actually true of the group in front of the reader — every member project id
// is in the caller's own owned set. If a member is somehow NOT owned by the
// caller (which at own scope should be impossible), the honest alternative is
// rendered instead, naming the situation. A sentence the screen cannot justify
// is precisely the failure this story exists to prevent, and "it should be
// impossible" is not a reason to print it unchecked.
export interface OwnNotifyBarProps {
  /** The member project ids of the group currently open, or [] when none is. */
  memberProjectIds: string[]
  /** Every project id the caller OWNS, from the getProjects() data the view
   * already holds (rows where shared_with_me is not true). */
  ownedProjectIds: string[]
}

export default function OwnNotifyBar({ memberProjectIds, ownedProjectIds }: OwnNotifyBarProps) {
  const owned = new Set(ownedProjectIds)
  const notOwned = memberProjectIds.filter((id) => !owned.has(id))
  const allOwned = memberProjectIds.length > 0 && notOwned.length === 0

  return (
    <div data-part="OwnNotifyBar" className="basis-full border-t border-dashed border-amber-200 pt-1.5 text-[11.5px]">
      {allOwned ? (
        <p className="text-gray-500">You are the lead on every project here — nothing to raise.</p>
      ) : notOwned.length > 0 ? (
        <p role="alert" className="text-amber-900">
          {notOwned.length === 1 ? 'One invention' : `${notOwned.length} inventions`} in this group
          {notOwned.length === 1 ? ' is' : ' are'} not yours, so you are not the lead on all of
          them. Raising this is not yours to do alone — and this screen will not tell you otherwise.
        </p>
      ) : (
        <p className="text-gray-500">No group is open, so there is no one to raise anything with yet.</p>
      )}
    </div>
  )
}
